import React from 'react';
import { TableHead, TableRow, TableCell, TableSortLabel } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import { IObject } from 'src/models/objectManagement';
import { useAppSelector } from 'src/app/hooks';

type Order = 'asc' | 'desc';

interface HeadCell {
  disablePadding: boolean;
  id: keyof IObject;
  label: string;
  size: number;
  sortAble: boolean;
  align?: 'center' | 'left' | 'right';
}

interface EnhancedTableProps {
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof IObject
  ) => void;
  order: Order;
  orderBy: string | number | symbol;
  rowCount: number;
}

const defaultHeadCells: HeadCell[] = [
  {
    id: 'objectName',
    disablePadding: false,
    label: 'Name',
    size: 20,
    align: 'center',
    sortAble: true
  },
  {
    id: 'createdAt',
    disablePadding: false,
    label: 'Date Added',
    size: 20,
    align: 'center',
    sortAble: true
  },
  {
    id: 'lastDataUpdated',
    disablePadding: false,
    label: 'Last Updated',
    size: 20,
    align: 'center',
    sortAble: true
  },
  {
    id: 'streamType',
    disablePadding: false,
    label: 'Type',
    size: 10,
    align: 'center',
    sortAble: true
  },
  {
    id: 'entityName',
    disablePadding: false,
    label: 'Entity Name',
    size: 10,
    align: 'center',
    sortAble: false
  },
  {
    id: 'status',
    disablePadding: false,
    label: 'Status',
    size: 10,
    align: 'center',
    sortAble: false
  }
];

const TableHeaderObjects: React.FC<EnhancedTableProps> = ({
  order,
  orderBy,
  onRequestSort
}) => {
  const customerType = useAppSelector(
    (state) => state.users.project.customerType
  );
  const isRetail = customerType === 'b2c';
  const createSortHandler =
    (property: keyof IObject) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  const headCells = isRetail
    ? defaultHeadCells
    : defaultHeadCells.filter((item) => item.id !== 'lastDataUpdated');

  return (
    <TableHead>
      <TableRow>
        <TableCell
          size="small"
          align="center"
          style={{ color: 'white', width: '5%' }}
          padding="none"
        >
          No
        </TableCell>
        {headCells.map((headCell) => (
          <React.Fragment key={headCell.id}>
            {headCell.sortAble ? (
              <TableCell
                key={uuidv4()}
                size="small"
                align={headCell.align}
                style={{ width: `${headCell.size}%` }}
                padding={headCell.disablePadding ? 'none' : 'normal'}
                sortDirection={orderBy === headCell.id ? order : false}
              >
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : 'asc'}
                  onClick={createSortHandler(headCell.id)}
                >
                  {orderBy === headCell.id ? (
                    <React.Fragment>
                      <div style={{ color: 'white', fontWeight: 700 }}>
                        {headCell.label}
                      </div>
                      <span
                        style={{
                          border: 0,
                          clip: 'rect(0 0 0 0)',
                          height: 1,
                          margin: -1,
                          overflow: 'hidden',
                          padding: 0,
                          position: 'absolute',
                          top: 20,
                          width: 1
                        }}
                      >
                        {order === 'desc'
                          ? 'sorted descending'
                          : 'sorted ascending'}
                      </span>
                    </React.Fragment>
                  ) : (
                    <div style={{ color: 'white' }}>{headCell.label}</div>
                  )}
                </TableSortLabel>
              </TableCell>
            ) : (
              <TableCell
                size="small"
                align={headCell.align}
                style={{ width: `${headCell.size}%` }}
                padding="none"
              >
                {headCell.label}
              </TableCell>
            )}
          </React.Fragment>
        ))}
        <TableCell
          size="small"
          align="center"
          style={{ color: 'white', width: '25%' }}
          padding="none"
        >
          Action
        </TableCell>
      </TableRow>
    </TableHead>
  );
};

export default TableHeaderObjects;
